import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Building2, MapPin, Sparkles, X } from 'lucide-react';
import { matchApi } from '@/entities/match';
import { useActiveEvent } from '@/entities/event';
import { UserAvatar } from '@/entities/user';
import { QUERY_KEYS, ROUTES } from '@/shared/config/constants';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  ScoreBadge,
  Skeleton,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';

const SCORE_DIMS = [
  { label: 'Tags compatíveis', value: 50, max: 50 },
  { label: 'Gargalo × oferta', value: 30, max: 40 },
  { label: 'Mesma área', value: 30, max: 30 },
  { label: 'Tomadores de decisão', value: 20, max: 20 },
  { label: 'Frequência em eventos', value: 12, max: 15 },
  { label: 'Nível de networking', value: 10, max: 10 },
];

const TABS = ['Sobre', 'Tags', 'Histórico'] as const;
type Tab = (typeof TABS)[number];

export const MatchDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('Sobre');
  const { eventoId } = useActiveEvent();

  const { data, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.MATCHES, eventoId] as const,
    queryFn: () => matchApi.list(eventoId ?? ''),
    enabled: !!eventoId,
    staleTime: 5 * 60 * 1000,
  });

  const match = useMemo(() => data?.matches.find((m) => m.id === id), [data, id]);

  const acceptMutation = useMutation({
    mutationFn: () => matchApi.accept(id ?? ''),
    onSuccess: () => {
      toast.success('Match aceito!');
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      navigate(ROUTES.APP.CHAT_CONVERSATION(id ?? ''));
    },
  });

  const skipMutation = useMutation({
    mutationFn: () => matchApi.skip(id ?? ''),
    onSuccess: () => {
      toast('Match pulado');
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      navigate(ROUTES.APP.MATCHES);
    },
  });

  if (isLoading) {
    return (
      <section
        className="mx-auto flex w-full max-w-2xl flex-col gap-5 pb-24"
        aria-busy="true"
        aria-label="Carregando match"
      >
        <Skeleton className="h-8 w-20 rounded-full" />
        <Card>
          <CardContent className="flex flex-col items-center gap-3 px-6 pt-8 text-center">
            <Skeleton className="size-20 rounded-full" />
            <div className="flex flex-col items-center gap-2">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-3 w-32" />
              <Skeleton className="mt-1 h-3 w-48" />
            </div>
            <Skeleton className="h-8 w-24 rounded-full" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-4 w-40" />
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-10" />
                </div>
                <Skeleton className="h-1.5 w-full rounded-full" />
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-border pt-3">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-16" />
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (!match) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">Match não encontrado.</p>
            <Button asChild variant="outline" className="mt-4">
              <Link to={ROUTES.APP.MATCHES}>Voltar para matches</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { participante } = match;

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-5 pb-24">
      <Button asChild variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
        <Link to={ROUTES.APP.MATCHES}>Voltar</Link>
      </Button>

      <Card>
        <CardContent className="flex flex-col items-center gap-3 px-6 pt-8 text-center">
          <UserAvatar nome={participante.nome} fotoUrl={participante.fotoUrl ?? null} size="xl" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">{participante.nome}</h1>
            <p className="text-sm text-muted-foreground">{participante.cargo}</p>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {participante.empresa ? (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> {participante.empresa}
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Campo Grande, MS
              </span>
            </div>
          </div>
          <ScoreBadge score={match.score} altoPotencial={match.altoPotencial} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-foreground" /> Score detalhado
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {SCORE_DIMS.map((dim) => {
            const pct = Math.round((dim.value / dim.max) * 100);
            return (
              <div key={dim.label} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">{dim.label}</span>
                  <span className="text-muted-foreground">
                    {dim.value}/{dim.max}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-foreground" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          <div className="flex items-center justify-between border-t border-border pt-3 text-sm font-semibold">
            <span>Total</span>
            <span>{match.score} / 165</span>
          </div>
        </CardContent>
      </Card>

      <div className="border-b border-border">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                tab === t
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        {tab === 'Sobre' ? (
          <Card>
            <CardContent className="space-y-4 px-5 py-5 text-sm">
              {participante.bio ? (
                <p className="leading-relaxed text-foreground/90">{participante.bio}</p>
              ) : null}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Motivos do match
                </p>
                <ul className="space-y-1.5 text-sm">
                  {match.motivos.map((m) => (
                    <li key={m} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        ) : null}
        {tab === 'Tags' ? (
          <Card>
            <CardContent className="space-y-4 px-5 py-5">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  O que busca
                </p>
                <div className="flex flex-wrap gap-2">
                  {participante.tagsBusca?.map((t) => (
                    <Chip key={t.id} tone="busca">
                      {t.nome}
                    </Chip>
                  )) ?? <p className="text-sm text-muted-foreground">Nenhuma tag.</p>}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  O que oferece
                </p>
                <div className="flex flex-wrap gap-2">
                  {participante.tagsOferta?.map((t) => (
                    <Chip key={t.id} tone="oferta">
                      {t.nome}
                    </Chip>
                  )) ?? <p className="text-sm text-muted-foreground">Nenhuma tag.</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}
        {tab === 'Histórico' ? (
          <Card>
            <CardContent className="px-5 py-5 text-sm text-muted-foreground">
              Match sugerido em {new Date(match.createdAt).toLocaleDateString('pt-BR')}. Sem outras
              interações registradas.
            </CardContent>
          </Card>
        ) : null}
      </div>

      <div className="fixed inset-x-0 bottom-16 z-30 border-t border-border bg-background p-3 lg:bottom-0 lg:left-[280px]">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2">
          <button
            type="button"
            onClick={() => skipMutation.mutate()}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" /> Pular
          </button>
          <Button
            fullWidth
            onClick={() => acceptMutation.mutate()}
            loading={acceptMutation.isPending}
          >
            Aceitar match
          </Button>
        </div>
      </div>
    </section>
  );
};
